import { UserPayload } from "../models/SiteModel";

const URLAPI = process.env.REACT_APP_BASE_URL;

const createHttpError = async (response: Response) => {
  const text = await response.text();
  return new Error(text || `HTTP ${response.status}`);
};

export type CompraProducto = {
  idProducto: number;
  codigo: string;
  producto: string;
  descripcion: string;
  presentacion: string;
  stock: number;
  precioCompra: number;
  precioCompraBruto: number;
  ivaCompra: number;
  ivaVenta: number;
  rubro: string;
  subrubro: string;
};

export type CompraProductoPage = {
  items: CompraProducto[];
  total: number;
  page: number;
  pageSize: number;
};

export type CompraReporteItem = {
  idCompra: number;
  fecha: string;
  fechaIva: string;
  idProveedor: number;
  proveedor: string;
  tipo: string;
  letraFactura: string;
  puntoVenta: string;
  numeroFactura: string;
  subtotal: number;
  iva105: number;
  iva21: number;
  iva27: number;
  noGravado: number;
  percepcionIva: number;
  ingresosBrutos: number;
  impuestosInternos: number;
  tem: number;
  bonificacion: number;
  totalFactura: number;
  formaPago: string;
  numeroRemito: string;
};

export type CompraReportePage = {
  items: CompraReporteItem[];
  total: number;
  page: number;
  pageSize: number;
  totalSubtotal: number;
  totalIva: number;
  totalPercepciones: number;
  totalBonificacion: number;
  totalFactura: number;
};

export type CompraLinea = {
  idProducto: number;
  producto: string;
  cantidad: number;
  ivaCompra: number;
  unitarioSinIva: number;
  unitarioConIva: number;
  totalConIva: number;
};

export type CompraGuardarRequest = {
  tipoComprobante: number;
  idLocal: number;
  puntoVenta: number;
  idProveedor: number;
  proveedor: string;
  numeroRemito: string;
  numeroFacturaPv: string;
  numeroFactura: string;
  letraFactura: string;
  fecha: string;
  fechaIva: string;
  noGravado: number;
  subtotal: number;
  iva21: number;
  iva105: number;
  iva27: number;
  exento: number;
  ingresosBrutos: number;
  impuestosInternos: number;
  tem: number;
  percepcionIva: number;
  bonificacion: number;
  totalFactura: number;
  formaPago?: number;
  vencimiento: string;
  detalle: CompraLinea[];
  vencimientos: { fecha: string; importe: number }[];
};

export async function fetchGetProductosCompra(
  idLocal: string | undefined,
  user: UserPayload,
  page: number,
  pageSize: number,
  filters: { search?: string; idProveedor?: number },
  signal?: AbortSignal
): Promise<CompraProductoPage> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (filters.search?.trim()) params.set("search", filters.search.trim());
  if (filters.idProveedor) params.set("idProveedor", String(filters.idProveedor));

  const response = await fetch(`${URLAPI}/Compra/Productos/${idLocal}?${params.toString()}`, {
    method: "GET",
    signal,
    headers: { Authorization: "Bearer " + user.token },
  });

  if (!response.ok) {
    throw await createHttpError(response);
  }

  const data = await response.json();
  return {
    items: data.items ?? data.Items ?? [],
    total: data.total ?? data.Total ?? 0,
    page: data.page ?? data.Page ?? page,
    pageSize: data.pageSize ?? data.PageSize ?? pageSize,
  };
}

export async function fetchGetReporteCompras(
  user: UserPayload,
  page: number,
  pageSize: number,
  filters: { fechaDesde: string; fechaHasta: string; idProveedor?: number },
  signal?: AbortSignal
): Promise<CompraReportePage> {
  const params = new URLSearchParams({
    fechaDesde: filters.fechaDesde,
    fechaHasta: filters.fechaHasta,
    page: String(page),
    pageSize: String(pageSize),
  });
  if (filters.idProveedor) params.set("idProveedor", String(filters.idProveedor));

  const response = await fetch(`${URLAPI}/Compra/Reporte?${params.toString()}`, {
    method: "GET",
    signal,
    headers: { Authorization: "Bearer " + user.token },
  });

  if (!response.ok) {
    throw await createHttpError(response);
  }

  const data = await response.json();
  return {
    items: data.items ?? data.Items ?? [],
    total: data.total ?? data.Total ?? 0,
    page: data.page ?? data.Page ?? page,
    pageSize: data.pageSize ?? data.PageSize ?? pageSize,
    totalSubtotal: data.totalSubtotal ?? data.TotalSubtotal ?? 0,
    totalIva: data.totalIva ?? data.TotalIva ?? 0,
    totalPercepciones: data.totalPercepciones ?? data.TotalPercepciones ?? 0,
    totalBonificacion: data.totalBonificacion ?? data.TotalBonificacion ?? 0,
    totalFactura: data.totalFactura ?? data.TotalFactura ?? 0,
  };
}

export async function fetchGuardarCompra(user: UserPayload, request: CompraGuardarRequest) {
  const response = await fetch(`${URLAPI}/Compra/Guardar`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + user.token,
    },
    body: JSON.stringify(request),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.message ?? data?.Message ?? "No se pudo guardar la compra.");
  }

  return data;
}
